import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiChevronDown, FiChevronRight, FiHelpCircle, FiSearch, FiX, FiLayers, FiFileText, FiArrowLeft } = FiIcons;

const UserGuide = ({ userType = 'company', onClose, isEmbedded = false }) => {
  const { guides } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [displayContent, setDisplayContent] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!guides) return;
    
    const categoryMap = { 'company': 'brukere', 'partner': 'partnere', 'seller': 'selgere', 'internal': 'internt', 'utreder': 'partnere' };
    const targetCategory = categoryMap[userType] || 'brukere';

    const rawGuides = guides.filter(g => {
        if (Array.isArray(g.categories)) {
            return g.categories.includes(targetCategory);
        }
        return g.category === targetCategory;
    });

    const sections = [
        { id: 'guide', title: 'Veiledere & Guider', icon: FiBook, description: 'Steg-for-steg instrukser', items: rawGuides.filter(g => g.type === 'guide') },
        { id: 'templates', title: 'Maler & Tekster', icon: FiLayers, description: 'Ferdig innhold', items: rawGuides.filter(g => g.type === 'template' || g.type === 'pitch') },
        { id: 'qna', title: 'Spørsmål & Svar', icon: FiHelpCircle, description: 'Ofte stilte spørsmål', items: rawGuides.filter(g => g.type === 'qna') }
    ].filter(s => s.items.length > 0);

    setDisplayContent(sections);
  }, [guides, userType]);

  const filteredSections = displayContent.map(section => ({
    ...section,
    items: section.items.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  const formatBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderLine = (line, i) => {
    if (!line.trim()) return <div key={i} className="h-4"></div>; // Empty line spacing
    
    if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-black text-gray-900 mt-8 mb-4 border-b pb-2">{line.replace(/^#\s/, '')}</h1>;
    if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">{line.replace(/^##\s/, '')}</h2>;
    if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-bold text-gray-900 mt-5 mb-2">{line.replace(/^###\s/, '')}</h3>;
    
    if (line.startsWith('- ') || line.startsWith('* ')) {
      return <li key={i} className="ml-6 list-disc mb-2 text-gray-700">{formatBold(line.substring(2))}</li>;
    }
    if (line.match(/^\d+\.\s/)) {
      return <li key={i} className="ml-6 list-decimal mb-2 text-gray-700">{formatBold(line.replace(/^\d+\.\s/, ''))}</li>;
    }
  
    return <p key={i} className="mb-3 text-gray-700 leading-relaxed">{formatBold(line)}</p>;
  };

  if (selectedItem) {
    return (
      <div className={isEmbedded ? "h-full bg-gray-50 flex flex-col" : "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"}>
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col ${isEmbedded ? 'flex-1 border border-gray-100' : 'max-w-3xl w-full max-h-[90vh]'}`}
        >
          <div className="bg-white px-6 py-5 border-b border-gray-100 flex justify-between items-center sticky top-0 z-10 shadow-sm">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSelectedItem(null)} 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
              >
                <SafeIcon icon={FiArrowLeft} className="text-xl" />
              </button>
              <h2 className="text-xl font-bold text-gray-900">{selectedItem.title}</h2>
            </div>
            {!isEmbedded && onClose && (
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-8 bg-white">
            <div className="max-w-2xl mx-auto">
              {/* Bytt ut bokstavelig \n med ekte linjeskift før vi splitter */}
              {selectedItem.content.replace(/\\n/g, '\n').split('\n').map((line, i) => renderLine(line, i))}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "h-full bg-gray-50 p-6 flex flex-col" : "fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"}>
      <div className={`bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col ${isEmbedded ? 'flex-1 border border-gray-100' : 'max-w-5xl w-full max-h-[90vh]'}`}>
        
        {/* Header */}
        <div className="bg-white px-8 py-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
              <span className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600"><SafeIcon icon={FiBook} /></span>
              Ressursbibliotek
            </h2>
            <p className="text-sm text-gray-500 mt-1 ml-13">Oppdatert dokumentasjon for {userType === 'company' ? 'bedrifter' : userType}</p>
          </div>
          {!isEmbedded && onClose && (
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
          )}
        </div>

        {/* Search */}
        <div className="px-8 py-6 bg-gray-50/50 border-b border-gray-100">
          <div className="relative max-w-lg">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-3.5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Søk i veiledere..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm transition-all"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          <div className="space-y-8 max-w-6xl mx-auto">
            {filteredSections.map((section) => (
              <div key={section.id}>
                <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <SafeIcon icon={section.icon} /> {section.title}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {section.items.map((item) => (
                    <motion.div 
                      key={item.id} 
                      whileHover={{ y: -2 }}
                      className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col h-full"
                      onClick={() => setSelectedItem(item)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                          <SafeIcon icon={FiFileText} />
                        </div>
                        <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-indigo-600" />
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2 group-hover:text-indigo-700">{item.title}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 mt-auto">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
            
            {filteredSections.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                <SafeIcon icon={FiSearch} className="text-4xl mx-auto mb-4 opacity-50" />
                <p>Ingen resultater funnet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;